/* eslint-disable @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access -- Disable all rules issued from linter */
import type {
  Editor,
  FileLoader,
  PluginConstructor,
  UploadAdapter as UploadAdapterInterface,
  UploadResponse,
} from 'ckeditor5';
import type { ImageUploadErrorTranslationKey } from '../types';

export type ImageUploadedStatuses = Record<string, boolean>;

export type ImageUploadMeta = Record<string, { height: number; width: number }>;

export type ImageUploadRequest = (uri: File) => Promise<{ url: string }>;

class UploadAdapter implements UploadAdapterInterface {
  loader: FileLoader;
  metaData: ImageUploadMeta;
  onError: (id: string, message: ImageUploadErrorTranslationKey) => void;
  onRequestUpload: ImageUploadRequest;
  onStart: (id: string) => void;
  onSuccess: (id: string) => void;

  constructor({
    loader,
    metaData,
    onHandleError,
    onHandleStart,
    onHandleSuccess,
    onRequestUpload,
  }: {
    loader: FileLoader;
    metaData: ImageUploadMeta;
    onHandleError: (id: string, message: ImageUploadErrorTranslationKey) => void;
    onHandleStart: (id: string) => void;
    onHandleSuccess: (id: string) => void;
    onRequestUpload: ImageUploadRequest;
  }) {
    // The file loader instance to use during the upload.
    this.loader = loader;
    this.onRequestUpload = onRequestUpload;
    this.onStart = onHandleStart;
    this.onSuccess = onHandleSuccess;
    this.onError = onHandleError;
    this.metaData = metaData;
  }

  // Aborts the upload process.
  abort() {
    this.onError(this.loader.id, 'fileUploadAborted');
    return false;
  }

  // Starts the upload process.
  async upload() {
    const { loader, metaData, onError, onRequestUpload, onStart, onSuccess } = this;
    // Return a promise that will be resolved when the file is uploaded.
    return loader.file.then(async (file) => {
      onStart(loader.id);

      return new Promise<UploadResponse>((resolve, reject) => {
        const img = new Image();

        if (!file) {
          onError(loader.id, 'fileAccessFailed');
          reject(new Error('File does not exist'));
          return;
        }

        img.addEventListener('load', async function () {
          try {
            const response = await onRequestUpload(file);
            metaData[response.url] = {
              height: img.height,
              width: img.width,
            };

            onSuccess(loader.id);

            resolve({
              default: response.url,
            });
          } catch {
            onError(loader.id, 'fileUploadFailed');
            reject(new Error('Failed to upload file'));
          }
        });

        img.addEventListener('error', function () {
          onError(loader.id, 'fileAccessFailed');
          reject(new Error('Failed to load file'));
        });

        img.src = URL.createObjectURL(file);
      });
    });
  }
}

// eslint-disable-next-line @typescript-eslint/naming-convention, max-params -- This is used as component, so we use PascalCase
function ImageUpload(
  onRequestUpload: ImageUploadRequest,
  onChangeUploading: (uploading: boolean) => void,
  // eslint-disable-next-line @typescript-eslint/no-invalid-void-type -- Return types must be defined to match interface
  onUpdateData: (editor: Editor) => undefined | void,
  onError: (key: ImageUploadErrorTranslationKey) => void,
  uploadData: Record<string, boolean>,
  metaData: ImageUploadMeta,
): PluginConstructor {
  const onHandleChangeUploading = () => {
    // eslint-disable-next-line unicorn/no-array-reduce -- Use reduce to determine uploading process state
    const isUploading = Object.values(uploadData).reduce(
      (uploading, uploadingImage) => uploading || uploadingImage,
      false,
    );
    onChangeUploading(isUploading);
  };

  const onHandleStart = (id: string) => {
    uploadData[id] = true;
    onHandleChangeUploading();
  };

  const onHandleSuccess = (id: string) => {
    uploadData[id] = false;
    onHandleChangeUploading();
  };

  const onHandleError = (id: string, message: ImageUploadErrorTranslationKey) => {
    uploadData[id] = false;
    onHandleChangeUploading();
    onError(message);
  };

  return function (editor: Editor) {
    editor.plugins.get('FileRepository').createUploadAdapter = (loader: FileLoader) =>
      new UploadAdapter({
        loader,
        metaData,
        onHandleError,
        onHandleStart,
        onHandleSuccess,
        onRequestUpload,
      });

    editor.conversion.for('downcast').add((dispatcher) => {
      dispatcher.on('attribute:src:imageBlock', (_, data, conversionApi) => {
        const viewWriter = conversionApi.writer;

        const url = data.attributeNewValue;
        const dimensions = metaData[url];

        if (dimensions) {
          viewWriter.setAttribute('imagewidth', dimensions.width, conversionApi.mapper.toViewElement(data.item));
          viewWriter.setAttribute('imageheight', dimensions.height, conversionApi.mapper.toViewElement(data.item));
          // eslint-disable-next-line sonarjs/elseif-without-else -- No else provided
        } else if (!uploadData[url]) {
          uploadData[url] = true;
          onHandleChangeUploading();

          const img = new Image();

          img.addEventListener('load', function () {
            metaData[url] = {
              height: img.height,
              width: img.width,
            };

            uploadData[url] = false;
            onHandleChangeUploading();
            onUpdateData(editor);
          });

          img.addEventListener('error', function () {
            uploadData[url] = false;
            onHandleChangeUploading();
          });

          img.src = url;
        }
      });
    });

    editor.data.upcastDispatcher.on('element:figure', (_, data) => {
      if (data.viewItem.childCount) {
        for (let index = 0; index < data.viewItem.childCount; index++) {
          const children = data.viewItem.getChild(index);

          if (children.name === 'img' && children.getAttribute('src')) {
            metaData[children.getAttribute('src')] = {
              height: data.viewItem.getAttribute('imageheight'),
              width: data.viewItem.getAttribute('imagewidth'),
            };
          }
        }
      }
    });
  };
}

export default ImageUpload;
/* eslint-enable @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access -- Disable all rules issued from linter */
